import { Check, Close } from '@mui/icons-material';
import { MarketplaceBankingSubProductTypeEnum } from '@repo/supergraph/marketplace/enums';
import type { BankingOffer, DriverType } from '@repo/supergraph/marketplace/types';

const APY_IDS = new Set(['apy1', 'apy3', 'apy5']);
const APY_ORDER = ['apy1', 'apy3', 'apy5'] as const;

export const isCdApyDriver = (driverId: string | undefined): boolean => {
  return driverId ? APY_IDS.has(driverId) : false;
};

export const splitCdApyDrivers = (drivers: DriverType[]) =>
  drivers.reduce(
    (acc, d) => {
      (APY_IDS.has(d.id) ? acc.cdApyDrivers : acc.otherDrivers).push(d);
      return acc;
    },
    { cdApyDrivers: [] as DriverType[], otherDrivers: [] as DriverType[] },
  );

export const getToneStyles = (value: boolean) => {
  if (value) {
    return {
      bg: 'greens.lightest',
      border: 'greens.lighter',
      iconBg: 'greens.lighter',
      Icon: Check,
      iconColor: 'success' as const,
    };
  }

  return {
    bg: 'reds.lightest',
    border: 'reds.lighter',
    iconBg: 'reds.lighter2',
    Icon: Close,
    iconColor: 'error' as const,
  };
};

export const isCheckingProduct = (entity: BankingOffer) =>
  entity.subProductType === MarketplaceBankingSubProductTypeEnum.CHECKING;

export const RELATED_PAGE_BY_SUBPRODUCT = {
  [MarketplaceBankingSubProductTypeEnum.SAVINGS]: {
    prefix: '» Looking for banks with the highest rates?',
    text: 'See our list of ',
    linkText: 'best high-yield online savings accounts',
    href: 'https://www.nerdwallet.com/banking/best/high-yield-online-savings-accounts',
  },
  [MarketplaceBankingSubProductTypeEnum.CD]: {
    prefix: '» COMPARE:',
    text: 'See our list of ',
    linkText: 'best CD rates',
    href: 'https://www.nerdwallet.com/banking/best/cd-rates',
  },
  [MarketplaceBankingSubProductTypeEnum.CHECKING]: {
    prefix: '» Looking for more checking account options?',
    text: "Check out NerdWallet's",
    linkText: 'best online checking accounts',
    href: 'https://www.nerdwallet.com/banking/best/online-checking-accounts',
  },
  [MarketplaceBankingSubProductTypeEnum.MONEY_MARKET]: {
    prefix: '» Looking for more money market account options?',
    text: "Check out NerdWallet's",
    linkText: 'best money market accounts',
    href: 'https://www.nerdwallet.com/banking/best/money-market-accounts',
  },
  [MarketplaceBankingSubProductTypeEnum.CASH_MANAGEMENT]: {
    prefix: '» Looking for more cash management account options?',
    text: "Check out NerdWallet's",
    linkText: 'best cash management accounts',
    href: 'https://www.nerdwallet.com/banking/best/cash-management-accounts',
  },
  [MarketplaceBankingSubProductTypeEnum.PREPAID]: {
    prefix: '» Looking for similar options?',
    text: "Check out NerdWallet's",
    linkText: 'best checking accounts',
    href: 'https://www.nerdwallet.com/banking/best/checking-accounts',
  },
} as const;

export const BANK_FEATURE_CONFIG = {
  sameDayTransfer: {
    title: 'Same-day transfer',
    description:
      'Allows users to move money quickly, ensuring funds are delivered within the same business day.',
    layout: 'stacked' as const,
  },
  earlyDirectDeposit: {
    title: 'Early direct deposit',
    description: 'Get access to paychecks up to two days early for faster financial flexibility.',
    layout: 'stacked' as const,
  },
  freeTransfers: {
    title: 'Free transfers',
    layout: 'inline' as const,
  },
  lineOfCredit: {
    title: 'Line of credit',
    layout: 'inline' as const,
  },
} as const;

export type DriverGroup = DriverType | DriverType[];

export const MAXIMUM_COLUMNS = 2;
/**
 *
 * @param drivers - drivers to group, where each inner array represents drivers for one column
 * @returns grouped drivers by id: eg. APY, Bonus, etc. CD APY drivers (apy1, apy3, apy5) are grouped together as a single group.
 *         Each group is an array indexed by column. For regular drivers: [driverFromCol0, driverFromCol1, ...]
 *         For CD APY drivers: [[driversFromCol0], [driversFromCol1], ...] where each element is an array of CD APY drivers
 * @example
 * groupDrivers([
 *   [
 *     { id: 'apy', value: '1.0%' },
 *     { id: 'apy', value: '1.5%' },
 *   ],
 * ]) // [[{ id: 'apy', value: '1.0%' }, { id: 'apy', value: '1.5%' }]]
 */
export const groupDrivers = (drivers: DriverType[][]): DriverGroup[][] => {
  // Split each column's drivers into CD APY and other drivers
  const columnSplits = drivers.map(columnDrivers => splitCdApyDrivers(columnDrivers));
  const otherDriversByColumn = columnSplits.map(split => split.otherDrivers);
  const cdApyDriversByColumn = columnSplits.map(split => split.cdApyDrivers);
  const hasCdApyDrivers = cdApyDriversByColumn.some(col => col.length > 0);

  // If any column has CD term APYs, we need to align regular "apy" drivers with the CD APY group
  // Extract regular "apy" drivers from otherDrivers when CD term APYs are present
  const regularApyDriversByColumn: (DriverType | undefined)[] = hasCdApyDrivers
    ? otherDriversByColumn.map(otherDrivers => {
        const apyDriver = otherDrivers.find(d => d.id === 'apy');
        return apyDriver;
      })
    : new Array(drivers.length).fill(undefined);

  // Remove regular "apy" drivers from otherDrivers if CD term APYs are present
  const filteredOtherDriversByColumn = hasCdApyDrivers
    ? otherDriversByColumn.map(otherDrivers => otherDrivers.filter(d => d.id !== 'apy'))
    : otherDriversByColumn;

  // Group other drivers by id across columns (excluding regular "apy" if CD term APYs exist)
  const groupedOtherDrivers: DriverGroup[][] = Object.values(
    filteredOtherDriversByColumn.flat().reduce<Record<string, DriverType[]>>((acc, driver) => {
      if (driver.id) {
        if (!acc[driver.id]) {
          acc[driver.id] = [];
        }
        acc[driver.id].push(driver);
      }
      return acc;
    }, {}),
  );

  // Group all CD APY drivers together as a single group per column
  // Ensure CD APY drivers are aligned by term (apy1, apy3, apy5) across columns
  // Include regular "apy" drivers in the same group row when CD term APYs are present
  const cdApyGroupByColumn: DriverGroup[] = cdApyDriversByColumn.map((colDrivers, colIndex) => {
    // If this column has CD term APYs, order them consistently
    if (colDrivers.length > 0) {
      const driverMap = new Map(colDrivers.map(driver => [driver.id, driver]));
      const orderedDrivers = APY_ORDER.map(id => driverMap.get(id)).filter(
        (driver): driver is DriverType => driver !== undefined,
      );
      return orderedDrivers.length > 0 ? orderedDrivers : colDrivers;
    }

    // If this column doesn't have CD term APYs but has a regular "apy" driver,
    // include it in the CD APY group row for alignment
    const regularApy = regularApyDriversByColumn[colIndex];
    if (regularApy) {
      return [regularApy];
    }

    // No APY drivers in this column
    return undefined as unknown as DriverGroup;
  });

  const result: DriverGroup[][] = [...groupedOtherDrivers];

  // Add CD APY drivers (and aligned regular "apy" drivers) as a single group if any column has them
  if (hasCdApyDrivers || regularApyDriversByColumn.some(apy => apy !== undefined)) {
    result.push(cdApyGroupByColumn);
  }

  return result;
};
